import type { GameKey } from './games.js'
import { SKILLS } from './games/maths-skills.js'
import { RIDDLE_SKILLS } from './games/riddle-skills.js'
import { MOSAIC_SKILLS } from './games/mosaic-skills.js'
import { SUDOKU_SKILLS } from './games/sudoku-skills.js'
import { TROLLEY_SKILLS } from './games/trolley-questions.js'

/**
 * Every skill any game records, as something a parent can read.
 *
 * The parent view is handed a skill key and turns it into a sentence, so it
 * needs one place that knows every key any game records.
 *
 * Turtley's Trolley was missing from that place in v1.0.0, because the lookup
 * was Monkey's own `skillByKey`. A parent read `trolley.total` where "Adding up
 * a basket" belonged.
 *
 * Three things stop the seventh game repeating it, because levels and attempts
 * are written down different paths and neither gate covers both:
 *
 * - `SKILLS_BY_GAME` is a `Record` over `SkillGame`, so a game added to that
 *   union does not compile until its skills are listed here. That gates the
 *   levels.
 * - `question_attempts.game_key` records which game asked, so a row is no
 *   longer a key with no provenance. That gates the reading.
 * - `isSkillOf` checks the key against the game named at the write. The column
 *   says which game wrote the row; this says the key belongs to it, and a row
 *   can be filed under the right game with a key that game does not have.
 */

/**
 * The games that record skills.
 *
 * A subset of `GameKey`, and deliberately its own type rather than all of them:
 * Hangman, Memory and Scramble record that a round happened and nothing finer,
 * so a map over every game would carry three empty entries pretending to be a
 * decision. `satisfies` keeps it honest against a typo or a renamed game.
 */
export const SKILL_GAMES = [
  'maths',
  'trolley',
  'riddles',
  'sudoku',
  'mosaic',
] as const satisfies readonly GameKey[]

export type SkillGame = (typeof SKILL_GAMES)[number]

/** As much of a skill as anything outside its own game needs. */
export interface LabelledSkill {
  readonly key: string
  /** Shown to a parent, not to the player. */
  readonly label: string
}

export const SKILLS_BY_GAME: Readonly<Record<SkillGame, readonly LabelledSkill[]>> = {
  maths: SKILLS,
  trolley: TROLLEY_SKILLS,
  riddles: RIDDLE_SKILLS,
  sudoku: SUDOKU_SKILLS,
  mosaic: MOSAIC_SKILLS,
}

/** Every skill in the app, whichever game records it. */
export const ALL_SKILLS: readonly LabelledSkill[] = SKILL_GAMES.flatMap(
  (game) => SKILLS_BY_GAME[game],
)

const LABEL_BY_KEY: ReadonlyMap<string, string> = new Map(
  ALL_SKILLS.map((skill) => [skill.key, skill.label]),
)

/**
 * The parent-facing name for a skill key.
 *
 * Falls back to the key rather than throwing. A row can outlive the skill that
 * wrote it — renamed, or dropped from a game — and one such row must not take
 * the whole page down with it. A key showing through is ugly and legible; a
 * blank progress page is neither.
 */
export function skillLabel(key: string): string {
  return LABEL_BY_KEY.get(key) ?? key
}

/**
 * Whether a key is one of this game's skills.
 *
 * The server checks this as it writes an attempt, against the game the writer
 * named. The row now carries that game too, so this is no longer standing in
 * for a missing column — the two say different things, and a key no game
 * claims is still a key that will show through as itself on the parent's page.
 *
 * Checked at the write rather than at the read, because a game's own route
 * tests run long before a parent opens the page. Trolley's went unnoticed for
 * two releases for want of exactly this.
 */
export function isSkillOf(game: SkillGame, key: string): boolean {
  return SKILLS_BY_GAME[game].some((skill) => skill.key === key)
}
